// @ts-check
import { gql } from '@apollo/client/core';
import { View } from '@react-pdf/renderer';
import { Title, Text } from '../../../pdf-design-system';
import { useProduct, useTheme } from './UrbanihofInfoSheet';
import React from 'react';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';

export default function UrbanihofInfoSheetProduct({ style, ...rest }) {
  return (
    <View style={style} {...rest}>
      {/* <ProductHeader style={{ marginBottom: '10mm' }} /> */}
      <ProductDescription style={{ marginBottom: '5mm' }} />
      <ProductDetails style={{ marginTop: 'auto' }} />
    </View>
  );
}

export function ProductHeader({ style, ...rest }) {
  const { product, wine } = useAfterQuery();
  const theme = useTheme();
  const { i18n } = useLingui();
  return (
    <View style={style} {...rest}>
      <Title variant="large">{product.nameLine1}</Title>
      <Title variant="large">{product.nameLine2}</Title>
      <Title style={{ color: theme.colors.primary, marginTop: 5 }}>
        {product.isCertifiedOrganic ? `${i18n._(t`Organic`)} ` : ''}
        {wine.vintage}
      </Title>
    </View>
  );
}

function ProductDescription({ style, ...rest }) {
  const { product } = useAfterQuery();
  return (
    <Text variant="large" style={style} {...rest}>
      {product.description}
    </Text>
  );
}

function ProductDetails({ style, ...rest }) {
  const { product, wine } = useAfterQuery();
  const { i18n } = useLingui();
  const analyticFormat = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  return (
    <View style={[{ borderTopWidth: 0.5, padding: '2mm' }, style]} {...rest}>
      <Value title={i18n._(t`Vinification`)}>{wine?.vinification}</Value>
      <Value title={i18n._(t`Origin`)}>{product?.origin}</Value>
      <Value title={i18n._(t`Variety`)}>{wine?.varietyNames}</Value>
      <Value title={i18n._(t`Harvest`)}>{wine?.harvestNotes}</Value>
      <Value title={i18n._(t`Classification`)}>{wine?.classification}</Value>
      <Value title={i18n._(t`Soil`)}>{wine?.soilType}</Value>
      <Value title={i18n._(t`Alcohol`)} isEmpty={product?.alcohol == null}>
        {i18n._(t`${i18n.number(product.alcohol, analyticFormat)}% ABV`)}
      </Value>
      <Value
        title={i18n._(t`Total acidity`)}
        isEmpty={wine?.totalAcidity == null}
      >
        {i18n.number(wine?.totalAcidity, analyticFormat)} g/l
      </Value>
      <Value
        title={i18n._(t`Residual sugar`)}
        isEmpty={wine?.residualSugar == null && !wine?.designation}
      >
        {[
          wine.residualSugar != null
            ? `${i18n.number(wine.residualSugar, analyticFormat)} g/l`
            : '',
          wine.designation,
        ]
          .filter(Boolean)
          .join(', ')}
      </Value>
      <Value
        title={i18n._(t`Aging potential`)}
        isEmpty={!wine?.drinkAfter && !wine?.drinkBefore}
      >
        {wine?.drinkAfter || ''}–{wine?.drinkBefore || ''}
      </Value>
      <Value
        title={i18n._(t`Drinking temperature`)}
        isEmpty={!wine?.minTemp && !wine?.maxTemp}
      >
        {[wine.minTemp, wine.maxTemp].filter(Boolean).join('–')} °C
      </Value>
      <Value title={i18n._(t`Food pairing`)}>{wine?.pairingDescription}</Value>
      <Value title={i18n._(t`Drinking recommendation`)}>
        {wine?.palateDescription}
      </Value>
    </View>
  );
}

function Value({ title, children, isEmpty = false }) {
  if (isEmpty) return null;
  if (children == null) return null;
  return (
    <View style={{ flexDirection: 'row', marginBottom: '1mm' }}>
      <Text variant="bold" style={{ width: '33mm', marginRight: '2mm' }}>
        {title}:
      </Text>
      <Text style={{ flex: 1 }}>{children}</Text>
    </View>
  );
}

UrbanihofInfoSheetProduct.fragment = gql`
  fragment UrbanihofInfoSheetProduct on Product {
    __typename
    productId
    shortName
    regionHierarchy
    subregion
    ... on Wine {
      vineyard
    }
    consumerDescription
    description
    isCertifiedOrganic
    alcohol
    totalAcidity
    residualSugar
    pairingDescription
    palateDescription
    ... on Wine {
      vintage
      classification
      vinification
      grapeVarieties {
        varietyName
      }
      harvestNotes
      soilType
      designation
      drinkBefore
      drinkAfter
      maxTemp
      minTemp
    }
  }
`;

function useAfterQuery() {
  /** @type {import('./@types/UrbanihofInfoSheetProduct').UrbanihofInfoSheetProductFragment} product */
  const rawProduct = useProduct();
  const rawWine = rawProduct.__typename === 'Wine' ? rawProduct : null;

  const origin = [...(rawProduct.regionHierarchy || []), rawWine?.vineyard]
    .filter(Boolean)
    .reverse()
    .join('/');
  const description = rawProduct.consumerDescription || rawProduct.description;

  const product = {
    ...rawProduct,
    nameLine1: nameLine1(rawProduct),
    nameLine2: nameLine2(rawProduct),
    nameLine3: nameLine3(rawProduct),
    origin,
    description,
  };
  const wine = rawWine && {
    ...rawWine,
    nameLine1: nameLine1(rawWine),
    nameLine2: nameLine2(rawWine),
    nameLine3: nameLine3(rawWine),
    origin,
    description,
    varietyNames: rawWine?.grapeVarieties
      ?.map(({ varietyName }) => varietyName)
      .filter(Boolean)
      .join(', '),
  };

  return { product, wine };
}

/** @param {import('./@types/UrbanihofInfoSheetProduct').UrbanihofInfoSheetProductFragment} product */
function nameLine1(product) {
  const wine = product.__typename === 'Wine' ? product : null;
  if (wine?.classification?.startsWith('DAC')) {
    const herkunft = `${wine.subregion
      ?.replace(wine.classification, '')
      .trim()} ${wine.classification}`.toUpperCase();
    return `${herkunft} ${wine.vineyard || ''}`;
  }
  // Fall back to empty line.
  return '';
}
/** @param {import('./@types/UrbanihofInfoSheetProduct').UrbanihofInfoSheetProductFragment} product */
function nameLine2(product) {
  const line1 = nameLine1(product);
  const name = line1.trim()
    ? product.shortName?.replace(line1, '').trim()
    : product.shortName;
  if (product.isCertifiedOrganic) {
    // Remove "BIO" from the name.
    return name.replace(/\s+bio\b/i, '');
  }
  return name;
}
/** @param {import('./@types/UrbanihofInfoSheetProduct').UrbanihofInfoSheetProductFragment} product */
function nameLine3(product) {
  if (product.isCertifiedOrganic) return 'Bio';
  return '';
}
